import { Delete } from "@payping/icons";
import { deleteClientClient, getClientClientList } from "@payping/redux/actions";
import { submitQueryParams } from "@payping/utils/query-string";
import { Modal, Space, Table, TableAction, Typography, useMessagedAsyncRetry } from "@payping/web-components";
import { ColumnsType } from "@payping/web-components/components/Table/types";
import qs from "query-string";
import React, { ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";

interface ClientType {
  clientName?: string;
  clientId?: string;
  description?: string;
}

interface ListQueryParamsType {
  pageNumber?: number;
  pageSize?: number;
}

const ClientsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const search: ListQueryParamsType = qs.parse(location.search, { parseNumbers: true });
  const parsedSearch = {
    ...search,
    pageNumber: search.pageNumber || 1,
    pageSize: search.pageSize || 10,
  };
  const { pageSize, pageNumber } = parsedSearch;
  const clients = useAppSelector((state) => state.client.client);
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();

  const { loading, retry } = useMessagedAsyncRetry(() => dispatch(getClientClientList()), [], { useProgress: true });

  const data = clients.list.map((client, index) => ({
    code: client.clientId,
    description: client.description,
    key: index,
    name: client.clientName,
  }));

  function handleDelete(rowIndex: number) {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("client.remove"),
      okText: t("ok"),
      onOk: async () => {
        await dispatch(deleteClientClient(data[rowIndex].code as string));
      },
    });
  }

  const actions = (rowIndex: number): ReactElement[] => [
    <a key={0}>
      <TableAction
        Icon={Delete}
        iconColor={"danger"}
        label={t("delete")}
        onClick={() => {
          handleDelete(rowIndex);
        }}
        key={1}
      />
    </a>,
  ];

  const cols: ColumnsType<ClientType> = [
    {
      dataIndex: "name",
      ellipsis: true,
      key: "name",
      title: t("client.table.name"),
    },
    { dataIndex: "description", key: "description", title: t("client.table.description") },
  ];

  return (
    <Space direction={"vertical"} size={"middle"} className={"tw-w-full"}>
      <Typography.Title level={4} className={"tw-font-medium"}>
        {t("profile.accesses")}
      </Typography.Title>
      <Table<ClientType>
        loading={!data.length ? loading : false}
        hasIdColumn
        actions={actions}
        dataSource={data}
        columns={cols}
        pagination={{
          onChange: (page, newSize): void => {
            const newValues = {
              ...parsedSearch,
              pageNumber: page,
              pageSize: newSize,
            };
            submitQueryParams({
              navigate,
              newValues,
              parsedSearch,
              pathname: location.pathname,
              retry,
            });
          },
          onShowSizeChange: (_currentPage, newSize): void => {
            const newValues = {
              ...parsedSearch,
              pageNumber: 1,
              pageSize: newSize,
            };
            submitQueryParams({
              navigate,
              newValues,
              parsedSearch,
              pathname: location.pathname,
              retry,
            });
          },
          pageNumber,
          pageSize,
          showTotal: (total, range): string => t("client.table.showTotal", { range: `${range[0]}-${range[1]}`, total }),
          total: clients.totalCount,
        }}
      />
      {contextHolder}
    </Space>
  );
};

export default ClientsList;
